import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Set up circuit laps for session analysis | Paddiq';

export function CircuitLapSetupPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Circuit lap guide</p>
            <h1>Set up circuit laps for a session.</h1>
            <p className="guide-hero__lead">
              Match a run to the correct saved circuit, build laps from its start/finish line, and
              save that timing for repeatable analysis.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/sessions')}
              >
                Open Sessions
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href={appPath('/circuits')}
              >
                Browse circuits
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/set-up-circuit-laps" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#open-setup">Open circuit setup</a>
                </li>
                <li>
                  <a href="#match">Match the circuit</a>
                </li>
                <li>
                  <a href="#verify">Verify the geometry</a>
                </li>
                <li>
                  <a href="#build">Build circuit laps</a>
                </li>
                <li>
                  <a href="#save">Save and confirm</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>Open a session with a GPS trace in a workspace where you can edit it.</span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="open-setup">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session &amp; laps</p>
                  <h2>Open circuit setup for the run.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong> and select the run you want to analyze.
                    </li>
                    <li>
                      In <strong>Session &amp; laps</strong>, switch from <strong>Raw laps</strong>{' '}
                      to <strong>Circuit Laps</strong>.
                    </li>
                    <li>
                      Select <strong>Circuit setup</strong>.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    Raw laps come from the logger. Circuit laps are rebuilt where the session GPS
                    trace crosses a saved circuit start/finish line.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="match">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Match</p>
                  <h2>Choose the correct saved configuration.</h2>
                  <p>
                    If Paddiq shows a best match, confirm the circuit and configuration before
                    selecting <strong>Use match</strong>. Otherwise, choose the circuit from{' '}
                    <strong>Circuit for this session</strong>.
                  </p>
                  <p>
                    Pay attention to layout and direction when a venue has more than one
                    configuration. The selected circuit is saved as part of the session context.
                  </p>
                  <div className="guide-callout">
                    Changing the circuit assignment or the saved configuration revision invalidates
                    circuit-timed laps that no longer match. Regenerate, review, and save the new
                    lap set before using it for comparison.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="verify">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Verify</p>
                  <h2>Check the saved geometry before trusting the lap split.</h2>
                  <p>
                    The guided setup shows the saved direction, start/finish status, and authored
                    turn-boundary count. The start/finish status must be <strong>Ready</strong> for
                    Paddiq to build circuit laps.
                  </p>
                  <p>
                    Select <strong>Check direction &amp; turns on map</strong> to inspect the
                    overlay against the session trace. Turn boundaries add corner context, but the
                    start/finish line is the required geometry for circuit lap timing.
                  </p>
                  <div className="guide-callout">
                    If start/finish is Missing, do not guess with a different configuration. Note
                    the circuit, layout, and direction, then contact support so the saved circuit
                    geometry can be reviewed.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="build">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Build laps</p>
                  <h2>Regenerate and review the detected laps.</h2>
                  <ol className="guide-instructions">
                    <li>
                      With a circuit assigned and start/finish Ready, select{' '}
                      <strong>Regenerate</strong>.
                    </li>
                    <li>Review the resulting circuit-lap count before saving.</li>
                    <li>
                      If no crossings are detected, confirm the layout and direction against the map
                      and compare with <strong>Raw laps</strong>.
                    </li>
                  </ol>
                  <p>
                    Regeneration is a draft until you save it. You can choose another circuit and
                    regenerate again without replacing the uploaded file or its original laps.
                  </p>
                  <p>
                    Paddiq can recover crossings from older sessions whose GPS clock resets during
                    the recording. A valid saved start/finish line and the exact saved circuit
                    configuration are still required.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="save">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Save</p>
                  <h2>Save the lap set and confirm the result.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select <strong>Save laps</strong>, or <strong>Update laps</strong> when a
                      saved set already exists.
                    </li>
                    <li>Wait for the saved date and time to appear.</li>
                    <li>Close setup and review the circuit laps in the session.</li>
                  </ol>
                  <div className="guide-ready">
                    <h3>Circuit setup is complete when:</h3>
                    <ul>
                      <li>the intended circuit and configuration are assigned;</li>
                      <li>start/finish shows Ready;</li>
                      <li>the generated laps match the run you expect; and</li>
                      <li>the guided setup says Circuit laps ready.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If circuit laps still are not ready</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>The session is read-only</h3>
                    <p>
                      Follow the access or billing guidance shown in Paddiq, or ask the workspace
                      owner to review it. Assigning a circuit and saving lap timing requires edit
                      access.
                    </p>
                  </article>
                  <article>
                    <h3>No circuit is a clear match</h3>
                    <p>
                      Browse Circuits for the venue and configuration. If it is absent, send support
                      the circuit name, layout, direction, and session date rather than selecting a
                      different layout.
                    </p>
                  </article>
                  <article>
                    <h3>No crossings are detected</h3>
                    <p>
                      Compare the GPS trace with the saved start/finish line and try the correct
                      configuration. Raw laps remain available when the source file contains them.
                    </p>
                  </article>
                  <article>
                    <h3>The saved lap count looks wrong</h3>
                    <p>
                      Do not save the draft. Record the selected circuit and expected lap count,
                      then use the session troubleshooting guide to collect the details support
                      needs.
                    </p>
                  </article>
                </div>
                <p>
                  For a broader checklist, read{' '}
                  <a className="guide-text-link" href="/resources/troubleshoot-session-uploads">
                    Troubleshoot session uploads and assignments
                  </a>
                  .
                </p>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready to build circuit laps?</p>
                  <h2>Open the session and follow the guided setup.</h2>
                  <p>Keep Raw laps available as a reference while you verify the circuit result.</p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/sessions')}
                  >
                    Open Sessions
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
